// components/tracker/calendar-hero.tsx
//
// Paper Calendar hero. The original shader renders natively and the desktop
// carve keeps fixed corner geometry while its right-hand paths follow width.
// Stats remain live and derive from the same calendar model as both views.

import { formatCtgovDate, humanizeDeltaDays } from "@/components/tracker/change-copy"
import { useDrugAvatar } from "@/components/tracker/drug-identity-context"
import { displayName } from "@/components/tracker/track-copy"
import { readoutPrecision, type ReadoutCalendar } from "@/components/tracker/readouts-view"
import { useEffect, useRef, useState } from "react"
import { StaticMeshGradient } from "@paper-design/shaders-react"

import styles from "@/components/tracker/readouts-page.module.css"

const CALENDAR_SHADER_COLOURS = ["#131D2C", "#235369", "#CC8766", "#F28351"]

function CalendarHeroArtwork() {
  const hostRef = useRef<HTMLDivElement>(null)
  const [canRenderShader, setCanRenderShader] = useState(false)

  useEffect(() => {
    const host = hostRef.current
    if (!host) return
    // Paper's renderer throws without WebGL2. Keep the CSS image underneath
    // during hydration, on unsupported devices and after context loss.
    const showFallback = () => setCanRenderShader(false)
    host.addEventListener("webglcontextlost", showFallback, true)
    try {
      const probe = document.createElement("canvas").getContext("webgl2")
      if (probe) {
        probe.getExtension("WEBGL_lose_context")?.loseContext()
        setCanRenderShader(true)
      }
    } catch {
      // The decorative image remains available when GPU access is blocked.
    }
    return () => host.removeEventListener("webglcontextlost", showFallback, true)
  }, [])

  return (
    <div ref={hostRef} className={styles.heroMesh} aria-hidden="true">
      {canRenderShader ? (
        <StaticMeshGradient
          className={styles.heroShader}
          colors={CALENDAR_SHADER_COLOURS}
          scale={1}
          rotation={0}
          positions={42}
          waveX={0.45}
          waveXShift={0.03}
          waveY={1}
          waveYShift={0}
          mixing={0}
          grainMixer={0.12}
          grainOverlay={0.46}
          speed={0}
          frame={0}
          minPixelRatio={2}
        />
      ) : null}
    </div>
  )
}

function CarveSvg({ width }: { width: number }) {
  const shift = width - 1147
  const r = (x: number) => x + shift
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width={width}
      viewBox={`0 0 ${width} 360`}
      className={styles.heroCarve}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d={`M0 312.465Q0 297.64 18.716 297.64H287.418C324.85 297.64 335.544 337.88 380.996 337.88H${width}V360H0Z`} fill="#FFFFFF" />
      <path d={`M-10.695 303.884H287.418C324.85 303.884 335.544 343.43 380.996 343.43H${width + 10.695}`} fill="none" stroke="#F8F9FA" strokeWidth="12" strokeLinecap="round" strokeLinejoin="round" />
      <path
        d={`M${r(1147)} 101.988C${r(1147)} 115.517 ${r(1114.916)} 115.517 ${r(1114.916)} 129.046V168.593C${r(1114.916)} 182.122 ${r(1147)} 182.122 ${r(1147)} 195.651L${r(1163.042)} 195.651 ${r(1163.042)} 101.988Z`}
        fill="#F8F9FA"
      />
      <path
        d="M-16.042-12.488V58.279H0V52.035Q0 29.139 29.41 29.139H252.661C286.082 29.139 286.082 0 319.502 0V-12.488Z"
        fill="#F8F9FA"
      />
      <path
        d={`M${r(728.572)}-12.488V0C${r(761.993)} 0 ${r(761.993)} 45.791 ${r(795.414)} 45.791H${r(1130.958)}Q${r(1147)} 45.791 ${r(1147)} 58.279V-12.488Z`}
        fill="#F8F9FA"
      />
    </svg>
  )
}

/** Approved Paper calendar illustration, decorative and hidden in narrow panes. */
function MiniCalendarMock() {
  return (
    <div className={styles.heroMock} aria-hidden="true" style={{ boxSizing: 'border-box', fontSynthesis: 'none', height: '236px', MozOsxFontSmoothing: 'grayscale', overflowWrap: 'anywhere', position: 'absolute', right: '61px', top: '62px', filter: 'none', WebkitFontSmoothing: 'antialiased', width: '340px' }}>
      <div style={{ backgroundColor: '#FFFFFF', borderColor: '#D9DEE5', borderRadius: '16px', borderStyle: 'solid', borderWidth: '1px', boxShadow: '#E5E9EE 0px 6px 0px, #14142429 0px 14px 28px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '8px', height: '172px', left: '24px', paddingBlock: '14px', paddingInline: '16px', position: 'absolute', top: '0px', width: '316px' }}>
        <div style={{ alignItems: 'center', borderBottomColor: '#E1E7EF', borderBottomStyle: 'solid', borderBottomWidth: '1px', boxSizing: 'border-box', display: 'flex', justifyContent: 'space-between', paddingBottom: '8px', width: '100%' }}>
          <div style={{ alignItems: 'center', boxSizing: 'border-box', display: 'flex', gap: '8px' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: '0' }}>
              <path d="M8 2v4M16 2v4" fill="none" stroke="#5F6368" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <rect x="3" y="4" width="18" height="18" rx="2" fill="none" stroke="#5F6368" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M3 10h18" fill="none" stroke="#5F6368" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div style={{ boxSizing: 'border-box', color: '#4B5563', fontFamily: '"Archivo", system-ui, sans-serif', fontSize: '12px', fontWeight: 500, lineHeight: '16px' }}>
              Your readout calendar
            </div>
          </div>
          <div style={{ boxSizing: 'border-box', display: 'flex', gap: '4px' }}>
            <div style={{ backgroundColor: '#F3F5F7', borderColor: '#E1E7EF', borderRadius: '6px', borderStyle: 'solid', borderWidth: '1px', boxSizing: 'border-box', flexShrink: '0', height: '20px', width: '20px' }} />
            <div style={{ backgroundColor: '#F3F5F7', borderColor: '#E1E7EF', borderRadius: '6px', borderStyle: 'solid', borderWidth: '1px', boxSizing: 'border-box', flexShrink: '0', height: '20px', width: '20px' }} />
          </div>
        </div>
        <div style={{ boxSizing: 'border-box', display: 'flex', width: '100%' }}>
          <div style={{ boxSizing: 'border-box', color: '#8A94A3', flexBasis: '0%', flexGrow: '1', fontFamily: '"Archivo", system-ui, sans-serif', fontSize: '12px', lineHeight: '16px' }}>
            M
          </div>
          <div style={{ boxSizing: 'border-box', color: '#8A94A3', flexBasis: '0%', flexGrow: '1', fontFamily: '"Archivo", system-ui, sans-serif', fontSize: '12px', lineHeight: '16px' }}>
            T
          </div>
          <div style={{ boxSizing: 'border-box', color: '#8A94A3', flexBasis: '0%', flexGrow: '1', fontFamily: '"Archivo", system-ui, sans-serif', fontSize: '12px', lineHeight: '16px' }}>
            W
          </div>
          <div style={{ boxSizing: 'border-box', color: '#8A94A3', flexBasis: '0%', flexGrow: '1', fontFamily: '"Archivo", system-ui, sans-serif', fontSize: '12px', lineHeight: '16px' }}>
            T
          </div>
          <div style={{ boxSizing: 'border-box', color: '#8A94A3', flexBasis: '0%', flexGrow: '1', fontFamily: '"Archivo", system-ui, sans-serif', fontSize: '12px', lineHeight: '16px' }}>
            F
          </div>
          <div style={{ boxSizing: 'border-box', color: '#8A94A3', flexBasis: '0%', flexGrow: '1', fontFamily: '"Archivo", system-ui, sans-serif', fontSize: '12px', lineHeight: '16px' }}>
            S
          </div>
          <div style={{ boxSizing: 'border-box', color: '#8A94A3', flexBasis: '0%', flexGrow: '1', fontFamily: '"Archivo", system-ui, sans-serif', fontSize: '12px', lineHeight: '16px' }}>
            S
          </div>
        </div>
        <div style={{ borderTopColor: '#E5E9EE', borderTopStyle: 'solid', borderTopWidth: '1px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', width: '100%' }}>
          <div style={{ boxSizing: 'border-box', display: 'flex', width: '100%' }}>
            <div style={{ borderBottomColor: '#E5E9EE', borderBottomStyle: 'solid', borderBottomWidth: '1px', borderRightColor: '#E5E9EE', borderRightStyle: 'solid', borderRightWidth: '1px', boxSizing: 'border-box', flexBasis: '0%', flexGrow: '1', height: '22px' }} />
            <div style={{ borderBottomColor: '#E5E9EE', borderBottomStyle: 'solid', borderBottomWidth: '1px', borderRightColor: '#E5E9EE', borderRightStyle: 'solid', borderRightWidth: '1px', boxSizing: 'border-box', flexBasis: '0%', flexGrow: '1', height: '22px' }} />
            <div style={{ borderBottomColor: '#E5E9EE', borderBottomStyle: 'solid', borderBottomWidth: '1px', borderRightColor: '#E5E9EE', borderRightStyle: 'solid', borderRightWidth: '1px', boxSizing: 'border-box', flexBasis: '0%', flexGrow: '1', height: '22px' }} />
            <div style={{ borderBottomColor: '#E5E9EE', borderBottomStyle: 'solid', borderBottomWidth: '1px', borderRightColor: '#E5E9EE', borderRightStyle: 'solid', borderRightWidth: '1px', boxSizing: 'border-box', flexBasis: '0%', flexGrow: '1', height: '22px' }} />
            <div style={{ borderBottomColor: '#E5E9EE', borderBottomStyle: 'solid', borderBottomWidth: '1px', borderRightColor: '#E5E9EE', borderRightStyle: 'solid', borderRightWidth: '1px', boxSizing: 'border-box', flexBasis: '0%', flexGrow: '1', height: '22px' }} />
            <div style={{ borderBottomColor: '#E5E9EE', borderBottomStyle: 'solid', borderBottomWidth: '1px', borderRightColor: '#E5E9EE', borderRightStyle: 'solid', borderRightWidth: '1px', boxSizing: 'border-box', flexBasis: '0%', flexGrow: '1', height: '22px' }} />
            <div style={{ borderBottomColor: '#E5E9EE', borderBottomStyle: 'solid', borderBottomWidth: '1px', boxSizing: 'border-box', flexBasis: '0%', flexGrow: '1', height: '22px' }} />
          </div>
          <div style={{ boxSizing: 'border-box', display: 'flex', width: '100%' }}>
            <div style={{ borderBottomColor: '#E5E9EE', borderBottomStyle: 'solid', borderBottomWidth: '1px', borderRightColor: '#E5E9EE', borderRightStyle: 'solid', borderRightWidth: '1px', boxSizing: 'border-box', flexBasis: '0%', flexGrow: '1', height: '22px' }} />
            <div style={{ borderBottomColor: '#E5E9EE', borderBottomStyle: 'solid', borderBottomWidth: '1px', borderRightColor: '#E5E9EE', borderRightStyle: 'solid', borderRightWidth: '1px', boxSizing: 'border-box', flexBasis: '0%', flexGrow: '1', height: '22px' }} />
            <div style={{ borderBottomColor: '#E5E9EE', borderBottomStyle: 'solid', borderBottomWidth: '1px', borderRightColor: '#E5E9EE', borderRightStyle: 'solid', borderRightWidth: '1px', boxSizing: 'border-box', flexBasis: '0%', flexGrow: '1', height: '22px' }} />
            <div style={{ borderBottomColor: '#E5E9EE', borderBottomStyle: 'solid', borderBottomWidth: '1px', borderRightColor: '#E5E9EE', borderRightStyle: 'solid', borderRightWidth: '1px', boxSizing: 'border-box', flexBasis: '0%', flexGrow: '1', height: '22px' }} />
            <div style={{ borderBottomColor: '#E5E9EE', borderBottomStyle: 'solid', borderBottomWidth: '1px', borderRightColor: '#E5E9EE', borderRightStyle: 'solid', borderRightWidth: '1px', boxSizing: 'border-box', flexBasis: '0%', flexGrow: '1', height: '22px' }} />
            <div style={{ borderBottomColor: '#E5E9EE', borderBottomStyle: 'solid', borderBottomWidth: '1px', borderRightColor: '#E5E9EE', borderRightStyle: 'solid', borderRightWidth: '1px', boxSizing: 'border-box', flexBasis: '0%', flexGrow: '1', height: '22px' }} />
            <div style={{ borderBottomColor: '#E5E9EE', borderBottomStyle: 'solid', borderBottomWidth: '1px', boxSizing: 'border-box', flexBasis: '0%', flexGrow: '1', height: '22px' }} />
          </div>
          <div style={{ boxSizing: 'border-box', display: 'flex', width: '100%' }}>
            <div style={{ borderRightColor: '#E5E9EE', borderRightStyle: 'solid', borderRightWidth: '1px', boxSizing: 'border-box', flexBasis: '0%', flexGrow: '1', height: '22px' }} />
            <div style={{ borderRightColor: '#E5E9EE', borderRightStyle: 'solid', borderRightWidth: '1px', boxSizing: 'border-box', flexBasis: '0%', flexGrow: '1', height: '22px' }} />
            <div style={{ borderRightColor: '#E5E9EE', borderRightStyle: 'solid', borderRightWidth: '1px', boxSizing: 'border-box', flexBasis: '0%', flexGrow: '1', height: '22px' }} />
            <div style={{ borderRightColor: '#E5E9EE', borderRightStyle: 'solid', borderRightWidth: '1px', boxSizing: 'border-box', flexBasis: '0%', flexGrow: '1', height: '22px' }} />
            <div style={{ borderRightColor: '#E5E9EE', borderRightStyle: 'solid', borderRightWidth: '1px', boxSizing: 'border-box', flexBasis: '0%', flexGrow: '1', height: '22px' }} />
            <div style={{ borderRightColor: '#E5E9EE', borderRightStyle: 'solid', borderRightWidth: '1px', boxSizing: 'border-box', flexBasis: '0%', flexGrow: '1', height: '22px' }} />
            <div style={{ boxSizing: 'border-box', flexBasis: '0%', flexGrow: '1', height: '22px' }} />
          </div>
        </div>
      </div>
      <div style={{ backgroundColor: '#FFFFFF', borderColor: '#D9DEE5', borderRadius: '14px', borderStyle: 'solid', borderWidth: '1px', boxShadow: '#E5E9EE 0px 6px 0px, #14142429 0px 14px 28px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '2px', height: '56px', justifyContent: 'center', left: '0px', paddingBlock: '10px', paddingInline: '14px', position: 'absolute', top: '52px', width: '196px' }}>
        <div style={{ alignItems: 'center', boxSizing: 'border-box', display: 'flex', gap: '8px' }}>
          <div style={{ backgroundColor: '#8A94A3', borderRadius: '999px', boxSizing: 'border-box', flexShrink: '0', height: '8px', width: '8px' }} />
          <div style={{ boxSizing: 'border-box', color: '#4B5563', fontFamily: '"Archivo", system-ui, sans-serif', fontSize: '12px', fontWeight: 500, lineHeight: '16px' }}>
            Upcoming readout
          </div>
        </div>
        <div style={{ boxSizing: 'border-box', paddingLeft: '16px' }}>
          <div style={{ boxSizing: 'border-box', color: '#5F6368', fontFamily: '"Archivo", system-ui, sans-serif', fontSize: '12px', lineHeight: '16px' }}>
            Sponsor estimate
          </div>
        </div>
      </div>
      <div style={{ borderLeftColor: '#C3CAD3', borderLeftStyle: 'dashed', borderLeftWidth: '1px', boxSizing: 'border-box', height: '32px', left: '186px', position: 'absolute', top: '146px', width: '0px' }} />
      <div style={{ backgroundColor: '#FFFFFF', borderColor: '#D9DEE5', borderRadius: '14px', borderStyle: 'solid', borderWidth: '1px', boxShadow: '#E5E9EE 0px 6px 0px, #14142429 0px 14px 28px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '6px', height: '60px', justifyContent: 'center', left: '108px', paddingBlock: '12px', paddingInline: '14px', position: 'absolute', top: '176px', width: '232px' }}>
        <div style={{ alignItems: 'center', boxSizing: 'border-box', display: 'flex', gap: '8px' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: '0' }}>
            <path d="M8 2v4M16 2v4" fill="none" stroke="#5F6368" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <rect x="3" y="4" width="18" height="18" rx="2" fill="none" stroke="#5F6368" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M3 10h18" fill="none" stroke="#5F6368" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <div style={{ boxSizing: 'border-box', color: '#4B5563', fontFamily: '"Archivo", system-ui, sans-serif', fontSize: '12px', fontWeight: 500, lineHeight: '16px' }}>
            Relevant conference
          </div>
        </div>
        <div style={{ backgroundColor: '#DADCE0', borderRadius: '999px', boxSizing: 'border-box', flexShrink: '0', height: '4px', width: '140px' }} />
      </div>
    </div>
  )
}

export function CalendarHero({
  cal,
  drugs,
  trialCount,
}: {
  cal: ReadoutCalendar
  /** The MERGED roster (listed + attributed): counted for the scope line
   *  only. The avatar's identity comes from the context, never from here. */
  drugs: string[]
  /** Fallback scope line for a watch with no attributable drugs. */
  trialCount: number
}) {
  const heroRef = useRef<HTMLElement>(null)
  const [carveWidth, setCarveWidth] = useState(1147)
  useEffect(() => {
    const hero = heroRef.current
    if (!hero) return
    const update = () => setCarveWidth(Math.round(hero.getBoundingClientRect().width))
    update()
    const observer = new ResizeObserver(update)
    observer.observe(hero)
    return () => observer.disconnect()
  }, [])
  const nextFace = useDrugAvatar(cal.next?.drug ?? null)
  return (
    <section ref={heroRef} className={styles.heroPlate} aria-label="Your catalyst calendar">
      <CalendarHeroArtwork />
      <CarveSvg width={carveWidth} />

      <div className={styles.heroPill}>
        <span className={styles.heroPillText}>Readouts</span>
      </div>

      <div className={styles.heroTitleBlock}>
        <h1 className={styles.heroTitle}>Your catalyst calendar</h1>
        <p className={styles.heroStandfirst}>
          When each of your trials expects to read out, soonest first.
        </p>
      </div>

      <div className={styles.heroStats}>
        <div className={`${styles.heroStat} ${styles.heroStatWide}`}>
          <p className={styles.heroStatKey}>Next readout</p>
          <p className={styles.heroStatVal}>
            {cal.next ? formatCtgovDate(cal.next.date) : "None scheduled"}
          </p>
          {cal.next?.drug && nextFace.src ? (
            <div className={styles.heroStatDrugRow}>
              <span
                className={styles.heroStatAvatar}
                data-identity={nextFace.identity ?? undefined}
                style={{
                  backgroundImage: `url("${nextFace.src}")`,
                }}
                aria-hidden="true"
              />
              <span className={styles.heroStatDrugText}>
                {displayName(cal.next.drug)} · {readoutPrecision(cal.next.date) === "day"
                  ? `in ${humanizeDeltaDays(cal.next.daysUntil)}`
                  : readoutPrecision(cal.next.date) === "month"
                    ? "day not recorded"
                    : "month and day not recorded"}
              </span>
            </div>
          ) : null}
        </div>

        <div className={styles.heroStat}>
          <p className={styles.heroStatKey}>Next 90 days</p>
          {cal.next90 === 0 ? (
            <>
              <p className={`${styles.heroStatVal} ${styles.heroStatValQuiet}`}>
                None
              </p>
              <p className={styles.heroStatSub}>No day-dated readouts</p>
            </>
          ) : (
            <>
              <p className={styles.heroStatVal}>
                {cal.next90.toLocaleString("en-GB")}
              </p>
              <p className={styles.heroStatSub}>
                {cal.next90 === 1 ? "day-dated readout" : "day-dated readouts"}
              </p>
            </>
          )}
        </div>

        <div className={styles.heroStat}>
          <p className={styles.heroStatKey}>Upcoming</p>
          <p className={styles.heroStatVal}>
            {cal.total.toLocaleString("en-GB")}
          </p>
          {/* Same rule as before the port: a watch with no listed drugs says
              what it does have rather than "across 0 drugs". */}
          <p className={styles.heroStatSub}>
            {drugs.length > 0
              ? `across ${drugs.length.toLocaleString("en-GB")} ${
                  drugs.length === 1 ? "drug" : "drugs"
                }`
              : `${trialCount.toLocaleString("en-GB")} ${
                  trialCount === 1 ? "trial" : "trials"
                } watched`}
          </p>
        </div>
      </div>

      <MiniCalendarMock />
    </section>
  )
}
